/**
 * Every facts box as one labelled table — the single source the chat
 * (overviewChatContext) and the file (overviewCsv) both read, so they cannot
 * drift (R19). Cells are raw numbers (float noise removed, never rounded);
 * NULL stays null, never 0, except a Result the platform did not report,
 * which is the page's "ไม่ได้รายงาน"; the file writes "—" where a value holds
 * nothing (boxTableParts.fileRows, rules-04 D4). Ratios, shares and deltas are
 * percent numbers; money headers name the currency; a Reach Result is named
 * "Reach รวมรายวัน", as D3's file names it (rules-04 D5). `kinds` says how the
 * chat shows each cell (as the page does). The cells, the box shell and the
 * box 2 / box 4 builders are the ones the Channel Deep-dive uses too (boxTableParts). Under
 * the card (`view.empty`): when the channels in view spent nothing (rule 04.3), or the
 * one ticked channel is known not to be connected, spend or not (rules-04 G3, H3),
 * every facts box says the card's sentence and nothing else (`notesOnly`, rules-04 H
 * review F2, I2). Pure.
 *
 * @module services/overview/overviewTables
 */
import type { TFunction } from 'i18next';
import { emptySentence } from '@/services/channels/channelFormat';
import type { CsvCell } from '@/services/csv';
import type { AppTileState } from '@/hooks/useLookerAppTiles';
import type { BackfillState } from '@/hooks/useLookerBackfill';
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import type { DashboardCsvLabels } from '@/services/looker/lookerCsv';
import type { MetricGap } from '@/services/looker/lookerCoverage';
import { coverageLabelsFrom, type CoverageNotesInput } from '@/services/looker/lookerCoverageLabels';
import type { AppWidget } from '@/services/looker/lookerAppTiles';
import type { DateRange } from '@/services/looker/lookerDateRange';
import { formatTargetDelta, hasVerdict } from '@/services/looker/lookerTargetCells';
import type { TileResultMetric, TileTargets } from '@/services/looker/lookerTypes';
import type { ResultMappingInfo, ResultMetric } from '@/services/targets/resultMetric';
import { platformLabel } from '@/utils/platformLabel';
import { bare, each, METRIC_KINDS, metricCells, metricColumns, num, pct, scorecardTable, shell, trendTable, unit } from './boxTableParts';
import { objectiveText, resultMetricName } from './overviewFormat';
import type { CurrencyChoice, OverviewWindows, ResultState } from './overviewTypes';
import type { OverviewView } from './overviewView';

/** The locked D1 order (spec §3.2); app boxes sit between the facts boxes. */
export const OVERVIEW_LAYOUT = ['2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13'] as const;
export const APP_BOX: Readonly<Partial<Record<string, AppWidget>>> = { 6: 'budgetPacing', 7: 'attention', 8: 'dataQuality', 10: 'outcomes' };

export type BoxStatus = 'ok' | 'empty' | 'failed' | 'loading' | 'needsWorkspace';
/**
 * How a cell is shown to the chat, as the page renders it; the file keeps the raw number. 'word' is a value the page
 * words (a target status, a tile's state); 'text' is a label, a dimension or a marker. The file writes "—" for a
 * value of any kind but 'text' that holds nothing (rules-04 D4). 'money4' is money at exactly four decimals: CPV
 * (rules-04 I1).
 */
export type CellKind = 'text' | 'word' | 'money' | 'money4' | 'count' | 'pct';

export interface BoxTable {
  /** The D1 box number. */
  id: string;
  title: string;
  status: BoxStatus;
  columns: string[];
  rows: CsvCell[][];
  /** One kind per cell of each row (box 2 differs per row). */
  kinds: CellKind[][];
  /** Worded notes the page shows beside the box (coverage chips, the no-spend list). */
  notes: string[];
  /**
   * Set only on an empty box under either card: the file writes its notes, the card's words, and not
   * "ไม่มีข้อมูลในช่วงนี้", so every card reads alike (rules-04 H review F2, I2).
   */
  notesOnly?: boolean;
  /** Boxes 3, 9, 13: Result is counted through this day; set only when the cut is in force. */
  resultThrough?: string;
  resultPrevious?: DateRange;
  /** Boxes 3 and 9: as /looker attaches them; null = all workspaces (asks for one). */
  targets?: TileTargets | null;
  resultMetric?: TileResultMetric;
}

/** Everything the chat and the file say about the page, assembled once by the page. */
export interface OverviewPageInput {
  title: string;
  allWorkspaces: boolean;
  workspaceName: string | null;
  windows: OverviewWindows;
  latestDataDate: string | null;
  money: CurrencyChoice | null;
  mapping: ResultMappingInfo | null;
  /** Raw channel ids; [] = every channel. */
  channels: readonly string[];
  objectiveLabel: string;
  /** The filter bar as the user set it, worded (the period travels as `period`). */
  filters: Array<{ title: string; value: string }>;
  tables: BoxTable[];
  app: Record<AppWidget, AppTileState>;
  appTitles: Record<AppWidget, string>;
  coverage: CoverageNotesInput;
  backfill: BackfillState;
  freshness: ChannelFreshnessState;
}

export interface TablesInput {
  view: OverviewView;
  windows: OverviewWindows;
  mapping: ResultMappingInfo | null;
  t: TFunction;
  labels: DashboardCsvLabels;
}

/** Literal keys, so the locale integrity test sees them; the grid uses the same ones. */
export const appTitlesFrom = (t: TFunction): Record<AppWidget, string> => ({
  budgetPacing: t('overview.box.pacing'),
  attention: t('overview.box.attention'),
  dataQuality: t('overview.box.quality'),
  outcomes: t('overview.box.outcomes'),
});

/** The "Result definition" cell: resultMetricName (rules-04 D5), the words boxes 9 and 13 print under each Result (E3). */
const metricName = (m: ResultMetric | null, t: TFunction): CsvCell => (m === null ? null : resultMetricName(m, t));

/** rules-04 C4: "ไม่ได้รายงาน" where the page says it, as D3's file writes it; any other NULL stays null (the file's "—", D4). */
const resultCell = (r: { result: number | null; resultState: ResultState }, t: TFunction): CsvCell =>
  (r.resultState === 'notReported' ? t('overview.notReported') : num(r.result));

const TARGET_KINDS: CellKind[] = ['money', 'word'];

const targetHeaders = (targets: TileTargets | null, l: DashboardCsvLabels): string[] =>
  [targets?.platform ? l.targetColumnFor(platformLabel(targets.platform)) : l.targetColumn, l.targetStatusColumn];

/**
 * [target, status word + delta (+ the channel whose own target was used)], as lookerCsv writes them.
 * Twin of lookerCsv.targetCells (same cells from TileTargets, not a LookerTile row): change both together.
 */
const targetCells = (targets: TileTargets | null, objective: string, l: DashboardCsvLabels): CsvCell[] => {
  if (targets === null) return [null, l.needsWorkspace];
  const cell = Object.values(targets.byObjective).find((c) => c?.objective === objective);
  if (!cell) return [null, null];
  const word = l.targetStatus(cell.status, targets.currency, cell.setAgainst);
  const status = hasVerdict(cell) ? `${word} ${formatTargetDelta(cell.deltaPct)}` : word;
  const channel = cell.platform && !targets.platform ? ` (${platformLabel(cell.platform)})` : '';
  return [cell.target, `${status}${channel}`];
};

const gapNotes = (gaps: readonly MetricGap[], t: TFunction): string[] => {
  const labels = coverageLabelsFrom(t);
  return gaps.map((g) => labels.notReported(g.platform, g.metric));
};

export const overviewTablesOf = ({ view, windows, mapping, t, labels }: TablesInput): BoxTable[] => {
  const cur = view.money?.currency ?? null;
  const low = (on: boolean): CsvCell => (on ? t('overview.lowSample') : null);
  const cut = windows.cutApplied
    ? { resultThrough: windows.through, ...(windows.cutPrevious ? { resultPrevious: windows.cutPrevious } : {}) }
    : {};
  const resultMetricAt = (at: TileResultMetric['awarenessAt']): Pick<BoxTable, 'resultMetric'> =>
    (mapping ? { resultMetric: { ...mapping, awarenessAt: at } } : {});

  const scorecards = scorecardTable(view.scorecards, t('overview.box.scorecards'), cur, t);

  const result = view.result && shell('3', t('overview.box.result'), view.result, (card) => ({
    columns: [
      t('overview.col.objective'), t('overview.csv.resultMetric'), t('overview.result'), unit(t('overview.costPerResult'), cur),
      t('overview.csv.deltaResultPct'), t('overview.csv.deltaCprPct'), ...targetHeaders(card.targets, labels), t('overview.lowSample'),
    ],
    rows: [[
      objectiveText(card.objective, t), metricName(card.metric, t), resultCell(card, t), num(card.costPerResult),
      pct(card.resultDelta), pct(card.costPerResultDelta), ...targetCells(card.targets, card.objective, labels), low(card.lowSample),
    ]],
    kinds: [['text', 'text', 'count', 'money', 'pct', 'pct', ...TARGET_KINDS, 'text']],
    notes: gapNotes(card.gaps, t),
    targets: card.targets,
    ...cut,
    ...resultMetricAt(card.objective === 'awareness' ? 'card' : null),
  }));

  const trend = trendTable(view.trend, t('overview.box.trend'), cur, t);

  const share = shell('5', t('overview.box.share'), view.share, (s) => ({
    columns: [t('overview.col.channel'), unit(t('overview.metric.spend'), cur), unit(t('overview.csv.share'), '%')],
    rows: s.slices.map((x) => [platformLabel(x.platform), num(x.spend), pct(x.share)]),
    kinds: each(s.slices, ['text', 'money', 'pct']),
    notes: s.noSpend.length > 0 ? [t('overview.donut.noSpend', { list: s.noSpend.map(platformLabel).join(', ') })] : [],
  }));

  const objectives = shell('9', t('overview.box.objectives'), view.objectives, (table) => ({
    columns: [
      t('overview.col.objective'), unit(t('overview.metric.spend'), cur), unit(t('overview.csv.share'), '%'), t('overview.metric.impressions'),
      t('overview.metric.clicks'), unit(t('overview.metric.ctr'), '%'), unit(t('overview.metric.cpc'), cur), unit(t('overview.metric.cpm'), cur),
      t('overview.csv.resultMetric'), t('overview.result'), unit(t('overview.costPerResult'), cur), t('overview.csv.deltaCprPct'),
      ...targetHeaders(table.targets, labels), t('overview.lowSample'),
    ],
    rows: table.rows.map((r) => [
      objectiveText(r.objective, t), num(r.spend), pct(r.share), num(r.impressions), num(r.clicks), pct(r.ctr), num(r.cpc), num(r.cpm),
      metricName(r.metric, t), resultCell(r, t), num(r.costPerResult), pct(r.costPerResultDelta),
      ...targetCells(table.targets, r.objective, labels), low(r.lowSample),
    ]),
    kinds: each(table.rows, ['text', 'money', 'pct', 'count', 'count', 'pct', 'money', 'money', 'text', 'count', 'money', 'pct', ...TARGET_KINDS, 'text']),
    notes: table.rows.flatMap((r) => gapNotes(table.gaps[r.objective] ?? [], t)),
    targets: table.targets,
    ...cut,
    ...resultMetricAt('rows'),
  }));

  const channels = shell('11', t('overview.box.channels'), view.channels, (rows) => ({
    columns: [t('overview.col.channel'), ...metricColumns(t, cur), t('overview.lowSample')],
    rows: rows.map((r) => [platformLabel(r.platform), ...metricCells(r), low(r.lowSample)]),
    kinds: each(rows, ['text', ...METRIC_KINDS, 'text']),
  }));

  const accounts = shell('12', t('overview.box.accounts'), view.accounts, (rows) => ({
    columns: [t('overview.col.account'), t('overview.col.channel'), t('overview.col.currency'), ...metricColumns(t, cur), t('overview.lowSample')],
    rows: rows.map((r) => [r.label, platformLabel(r.platform), r.currency, ...metricCells(r), low(r.lowSample)]),
    kinds: each(rows, ['text', 'text', 'text', ...METRIC_KINDS, 'text']),
  }));

  const campaigns = shell('13', t('overview.box.campaigns'), view.campaigns, (rows) => ({
    columns: [
      t('overview.col.campaign'), t('overview.col.channel'), t('overview.col.objective'), unit(t('overview.metric.spend'), cur),
      t('overview.csv.resultMetric'), t('overview.result'), t('looker.coverage.unclassified'),
    ],
    rows: rows.map((r) => [
      r.name, platformLabel(r.platform), objectiveText(r.objective, t), num(r.spend), metricName(r.metric, t), resultCell(r, t),
      r.unclassified ? t('looker.coverage.unclassified') : null,
    ]),
    kinds: each(rows, ['text', 'text', 'text', 'money', 'text', 'count', 'text']),
    ...cut,
    ...resultMetricAt('rows'),
  }));

  const tables = [scorecards, ...(result ? [result] : []), trend, share, objectives, channels, accounts, campaigns];
  if (!view.empty) return tables;
  // Under either card every facts box says its sentence and nothing else, as on D4 (rules-04 H review F2, I2).
  const sentence = emptySentence(view.empty, t);
  return tables.map((b) => ({ ...bare(b.id, b.title, 'empty', [sentence]), notesOnly: true }));
};
